import { Box, Chip, LinearProgress } from '@mui/material';

import { BatchJobRef, Job } from '../../../types/syntiro_honey';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import { summariseBatchProgress } from './job_status';

type ChipColor = 'default' | 'info' | 'success' | 'error' | 'warning';

interface BatchProgressHeaderProps {
  /** One job reference per queued file, as returned by `processBatch`. */
  jobRefs: Array<BatchJobRef>;
  /** Last polled job state per job id (absent until the first poll lands). */
  jobsById: Record<string, Job>;
}

/**
 * Batch-level progress header above the per-file list: an overall
 * files-finished bar plus a per-state tally (`2 done · 1 processing · …`), so a
 * reviewer can read the whole batch's status at a glance without scanning every
 * row. A count chip is omitted when it is zero (cancelled is hidden unless it
 * happened) to keep the tally to the states that actually apply.
 * @param {Array<BatchJobRef>} jobRefs Job references being polled.
 * @param {Record<string, Job>} jobsById Last polled state per job id.
 * @returns {JSX.Element}
 */
export default function BatchProgressHeader({ jobRefs, jobsById }: BatchProgressHeaderProps) {
  const summary = summariseBatchProgress(jobRefs, jobsById);
  const tallies: Array<{ key: string; count: number; label: string; color: ChipColor }> = [
    { key: 'done', count: summary.done, label: 'done', color: 'success' },
    { key: 'processing', count: summary.processing, label: 'processing', color: 'info' },
    { key: 'queued', count: summary.queued, label: 'queued', color: 'default' },
    { key: 'failed', count: summary.failed, label: 'failed', color: 'error' },
    { key: 'cancelled', count: summary.cancelled, label: 'cancelled', color: 'warning' }
  ];
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1
        }}
      >
        <BodyText weight="Medium">
          {`${summary.finished} of ${summary.total} file${summary.total === 1 ? '' : 's'} processed`}
        </BodyText>
        <CaptionText sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {`${summary.percentComplete}%`}
        </CaptionText>
      </Box>
      <LinearProgress variant="determinate" value={summary.percentComplete} sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {tallies
          .filter((tally) => tally.count > 0 || tally.key !== 'cancelled')
          .map((tally) => (
            <Chip
              key={tally.key}
              size="small"
              variant="outlined"
              color={tally.color}
              label={`${tally.count} ${tally.label}`}
            />
          ))}
      </Box>
    </Box>
  );
}
